import { Box } from '@mui/material';
import { InfoBox } from '@resurs-internal/mui';
import { useState } from 'react';

import Description from '../../components/Description/Description';
import { ErrorMessage } from '../../components/ErrorMessage';
import IntegerField from '../../components/IntegerField/IntegerField';
import PagesFooter from '../../components/PagesFooter/PagesFooter';
import PagesHeader from '../../components/PagesHeader/PagesHeader';
import Subtitle from '../../components/Subtitle/Subtitle';
import Title from '../../components/Title/Title';
import { usePagesContext } from '../../context/PagesContext';
import { scrollToFieldWithError } from '../../helpers/scrollToFieldWithError';
import { useAppDispatch, useAppSelector } from '../../hooks';
import useArnFromPath from '../../hooks/useArnFromPath';
import useComponentTracker from '../../hooks/useComponentTracker';
import useDefaultPagesAction from '../../hooks/useDefaultPagesAction';
import useEnrichAccountInformationService from '../../hooks/useEnrichAccountInformationService';
import useGetSelectedConsolidations from '../../hooks/useGetSelectedConsolidations';
import { usePageFocus } from '../../hooks/usePageFocusHook';
import usePagesConfig from '../../hooks/usePagesConfig';
import useSetBannerType from '../../hooks/useSetBannerType';
import useTranslation from '../../hooks/useTranslation';
import useValidation from '../../hooks/useValidation';
import { getApplicationState } from '../../store/application/application';
import { addOrUpdateFormField } from '../../store/applicationform';
import { selectFieldStateByName } from '../../store/applicationform/applicationFormSelectors';
import { WizardPageAction } from '../../store/wizard/wizard';

const AccountInfoPageNO = () => {
    useComponentTracker('AccountInfoPage');

    useSetBannerType('bannerThree');

    usePageFocus();
    const { totalSections } = usePagesConfig();
    const dispatch = useAppDispatch();
    const translate = useTranslation();
    const [isError, setIsError] = useState(false);
    const enrichAccountInformationService = useEnrichAccountInformationService();
    const validationErrors = useValidation(['payoutAccountNumber']);
    const { currentPage } = usePagesContext();
    const defaultPagesAction = useDefaultPagesAction();
    const accountNumberField = useAppSelector(selectFieldStateByName)('payoutAccountNumber');
    const applicationReferenceNumber = useArnFromPath();
    const { selectedConsolidations } = useGetSelectedConsolidations();
    const isConsolidationLoan = (selectedConsolidations?.consolidations?.length ?? 0) > 0;

    const onAction = async (action: WizardPageAction) => {
        if (action === 'next' && validationErrors.length) {
            scrollToFieldWithError(validationErrors);
            validationErrors.forEach((validationError) => {
                dispatch(addOrUpdateFormField(validationError));
            });
            return false;
        }

        try {
            setIsError(false);
            await enrichAccountInformationService(accountNumberField?.value + '');
            await dispatch(getApplicationState({ applicationReferenceNumber }));
            return true;
        } catch (error) {
            setIsError(true);
            throw error;
        }
    };

    return (
        <>
            <PagesHeader
                actions={[]}
                onAction={defaultPagesAction}
                currentPage={currentPage}
                currentSection={3}
                numberOfSections={totalSections}
                numberOfPagesInSection={4}
                currentPageInSection={0}
                sectionTitle={translate('field.section.your-account')}
            />
            <Title text={translate('page.account-information.title')} />
            <Description text={translate('page.account-information.description')} />
            {isConsolidationLoan && (
                <Description text={translate('page.account-information.description.consolidation')} />
            )}
            <IntegerField
                name="payoutAccountNumber"
                label={translate('field.account-information.account-number')}
                validation={{ format: '^\\d{11}$', formatErrorKey: 'validation.error.account-number.format' }}
            />
            <Subtitle text={translate('page.account-information.body-subtitle')} />
            <Description text={translate('page.account-information.body-description')} />
            <Box mt={2} mb={2}>
                <InfoBox variant="no-title">
                    {translate('page.account-information.body-additional-information')}
                </InfoBox>
            </Box>
            {isError && (
                <ErrorMessage title={translate('page.error.title')} description={translate('page.error.description')} />
            )}
            <PagesFooter
                actions={['next']}
                onAction={onAction}
                currentSection={3}
                numberOfSections={totalSections}
                numberOfPagesInSection={4}
                currentPageInSection={0}
                sectionTitle={translate('field.section.your-account')}
            />
        </>
    );
};

export default AccountInfoPageNO;
